/**
 * كل تحويل تاريخ للعرض يمر من هنا وحده (ADR §5 وfrontend-design §2).
 *
 * **من أين تأتي المنطقة الزمنية (قرار D30):** معرّفها مذكور مرة واحدة في
 * `packages/domain/src/time.ts` ولا يُكرَّر في أي ملف TypeScript آخر — ولذلك لا
 * يكتبه هذا الملف حرفيًا. الخادم يعلنه في حمولة `GET /views/agenda`
 * (`{ date, timezone, … }`)، وشاشة «اليوم» تثبّته هنا بـ `setAppTimeZone` عند أول
 * استجابة. قبل ذلك يُنسَّق بمنطقة الجهاز — وهي منطقة المالك نفسها عمليًا.
 */

let appTz: string | undefined;

/**
 * **الأرقام لاتينية دائمًا** — ثابت لا متغيّر (بأمر المالك 2026-08-04: «خلّي
 * دائمًا الأرقام 123»). وكان مفتاح إعداد بمبدّل في الشاشة، فحُذف المبدّل وثُبّتت
 * القيمة هنا.
 *
 * **وهو ثابتٌ لا متغيّرٌ بقيمة ابتدائية** عمدًا: مُتغيِّرٌ بلا مُسنِد اليوم يبقى
 * بابًا مفتوحًا يُسنَد إليه غدًا بلا قرار. وذكرُه صريحًا في `locale()` ضروري لأن
 * `ar-SA` وحدها تُخرج أرقامًا **هندية** (قِيس: `١١:٤٥` مقابل `11:45`) — فإسقاط
 * اللاحقة لا يعطي لاتينية بل عكسها.
 */
const NUMERAL_SYSTEM = 'latn';

/** يُستدعى مرة من استجابة الأجندة — المصدر الوحيد لمنطقة المالك في العميل. */
export function setAppTimeZone(timeZone: string): void {
  if (timeZone.length > 0) appTz = timeZone;
}

export function appTimeZone(): string | undefined {
  return appTz;
}

function locale(): string {
  return `ar-SA-u-ca-gregory-nu-${NUMERAL_SYSTEM}`;
}

function zoned<T extends Intl.DateTimeFormatOptions>(options: T): Intl.DateTimeFormatOptions {
  return appTz === undefined ? options : { ...options, timeZone: appTz };
}

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

/* ── تنسيق ─────────────────────────────────────────────────────────────────── */

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(locale()).format(value);
}

/** "الثلاثاء 29 يوليو" — رأس «اليوم» ورؤوس أيام الخطوط الزمنية. */
export function formatDayHeading(value: string | Date): string {
  return new Intl.DateTimeFormat(
    locale(),
    zoned({ weekday: 'long', day: 'numeric', month: 'long' }),
  ).format(toDate(value));
}

/** "9 أغسطس 2026" — الميلاديّ كاملًا بسنته، للشريط العلويّ. */
export function formatGregorianFull(value: string | Date): string {
  return new Intl.DateTimeFormat(
    locale(),
    zoned({ day: 'numeric', month: 'long', year: 'numeric' }),
  ).format(toDate(value));
}

/**
 * أسماء الشهور الهجرية **مثبَّتة** لا متروكة لـICU.
 *
 * وهذا منقولٌ عن bestversion بعد قياس علّته: تهجئة ICU للشهر تختلف بين Node
 * والمتصفّح («ذو القعدة» مقابل «ذو القعدة» بصيغٍ متفاوتة)، فيتغيّر النصّ
 * بتغيّر بيئة التنفيذ. فيُقرأ الشهر **رقمًا** ويُبدَّل من هنا.
 */
const HIJRI_MONTHS_AR = [
  'محرّم',
  'صفر',
  'ربيع الأول',
  'ربيع الآخر',
  'جمادى الأولى',
  'جمادى الآخرة',
  'رجب',
  'شعبان',
  'رمضان',
  'شوّال',
  'ذو القعدة',
  'ذو الحجّة',
] as const;

/**
 * "الأحد 26 صفر" — التاريخ الهجريّ **للعرض في الشريط العلويّ وحده**.
 *
 * ## قرار المالك الحاكم (‏2026-08-09، نصًّا)
 *
 * > «فيها تاريخ هجري وميلادي. التاريخ المعتمد في نظام فلو هو الميلادي، أما
 * > الهجري هو **فقط للعرض في هذا المكان ولا تقم بوضعه في أي مكان أخر**».
 *
 * فهذه الدالّة **ليست أداةً عامّة**: كل ما عداها في فلو ميلاديّ — الأجندة
 * والمواعيد والتذكيرات ومفاتيح الأيام والفرز. واستيرادُها في شاشةٍ أخرى نقضٌ
 * للقرار، ويحرسه `check:hijri-scope`.
 *
 * **وبلا سنة** عمدًا (كما في المصدر): الشريط ضيّق، والسنة الهجرية لا تُضيف
 * للمالك شيئًا وهو يقرأ الميلاديّ بسنته بجوارها.
 *
 * **وبلا مكتبة**: `Intl` الأصلية بتقويم أمّ القرى — وbestversion أزال
 * `moment-hijri` (~70KB) لصالحها، وذلك مكسبٌ في ميزانية حزمتنا كذلك.
 */
export function formatHijriDisplay(value: string | Date = new Date()): string {
  const parts = new Intl.DateTimeFormat(
    `ar-SA-u-ca-islamic-umalqura-nu-${NUMERAL_SYSTEM}`,
    zoned({ weekday: 'long', day: 'numeric', month: 'numeric' }),
  ).formatToParts(toDate(value));

  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const monthIndex = Number.parseInt(read('month'), 10) - 1;
  const month = HIJRI_MONTHS_AR[monthIndex] ?? read('month');
  return `${read('weekday')} ${read('day')} ${month}`;
}

/** "09:30" — بأرقام لاتينية دائمًا وtabular-nums في العرض. */
export function formatTime(value: string | Date): string {
  return new Intl.DateTimeFormat(
    locale(),
    zoned({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  ).format(toDate(value));
}

/** التلميح المطلق الكامل بتوقيت المالك. */
export function formatAbsolute(value: string | Date): string {
  return new Intl.DateTimeFormat(
    locale(),
    zoned({
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }),
  ).format(toDate(value));
}

const relative = (): Intl.RelativeTimeFormat =>
  new Intl.RelativeTimeFormat(locale(), { numeric: 'auto', style: 'long' });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** نسبي دائمًا في المتون: "قبل ساعتين"، "أمس"، "خلال 3 أيام". */
export function formatRelative(value: string | Date, now: Date = new Date()): string {
  const diff = toDate(value).getTime() - now.getTime();
  const abs = Math.abs(diff);
  const fmt = relative();

  if (abs < MINUTE) return 'الآن';
  if (abs < HOUR) return fmt.format(Math.round(diff / MINUTE), 'minute');
  if (abs < DAY) return fmt.format(Math.round(diff / HOUR), 'hour');
  if (abs < 30 * DAY) return fmt.format(Math.round(diff / DAY), 'day');
  if (abs < 365 * DAY) return fmt.format(Math.round(diff / (30 * DAY)), 'month');
  return fmt.format(Math.round(diff / (365 * DAY)), 'year');
}

/** عمر الانتظار/الركود بالأيام الكاملة — رقم لا نص. */
export function ageInDays(value: string | Date, now: Date = new Date()): number {
  return Math.floor((now.getTime() - toDate(value).getTime()) / DAY);
}

export function isOverdue(value: string | null, now: Date = new Date()): boolean {
  return value !== null && new Date(value).getTime() < now.getTime();
}

/* ── حساب الجدار الزمني في منطقة المالك ─────────────────────────────────────── */

interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function wallClockOf(instant: Date, timeZone: string | undefined): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(timeZone === undefined ? {} : { timeZone }),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    return found === undefined ? 0 : Number.parseInt(found.value, 10);
  };

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

function offsetMs(instant: Date, timeZone: string | undefined): number {
  const wall = wallClockOf(instant, timeZone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  return asUtc - instant.getTime();
}

/**
 * لحظة مطلقة من ساعة جدار في منطقة المالك. المرور مرتين يعالج مناطق DST؛
 * منطقة المالك بلا DST فالنتيجة مضبوطة من المرة الأولى.
 */
function instantFromWallClock(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const first = offsetMs(new Date(guess), appTz);
  const candidate = new Date(guess - first);
  const second = offsetMs(candidate, appTz);
  return first === second ? candidate : new Date(guess - second);
}

/** ساعة الجدار الحالية عند المالك — تحية الرأس تُشتق منها لا من ساعة الجهاز. */
export function ownerHour(now: Date = new Date()): number {
  return wallClockOf(now, appTz).hour;
}

/** "صباح النور" / "مساء النور" — §5.9-5. */
export function greeting(now: Date = new Date()): string {
  return ownerHour(now) < 12 ? 'صباح النور' : 'مساء النور';
}

/** مفتاح اليوم `YYYY-MM-DD` بمنطقة المالك — أساس تجميع الخطوط الزمنية بالأيام. */
export function dayKey(value: string | Date): string {
  const wall = wallClockOf(toDate(value), appTz);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(wall.year)}-${pad(wall.month)}-${pad(wall.day)}`;
}

/** رأس مجموعة يومية: "اليوم" / "أمس" / التاريخ. */
export function dayLabel(value: string | Date, now: Date = new Date()): string {
  const key = dayKey(value);
  if (key === dayKey(now)) return 'اليوم';
  if (key === dayKey(new Date(now.getTime() - DAY))) return 'أمس';
  return formatDayHeading(value);
}

/* ── أهداف الغفوة الموحدة (قرار D40) ────────────────────────────────────────── */

export interface SnoozeTarget {
  readonly id: string;
  readonly label: string;
  /** ISO مطلق — الخادم لا يفسر نصًا زمنيًا. */
  readonly at: string;
}

const MORNING_HOUR = 9;
/** getUTCDay: الأحد = 0 — بداية أسبوع العمل عند المالك. */
const SUNDAY = 0;

function tomorrowMorning(now: Date): Date {
  const wall = wallClockOf(now, appTz);
  const nextDay = new Date(Date.UTC(wall.year, wall.month - 1, wall.day) + DAY);
  return instantFromWallClock(
    nextDay.getUTCFullYear(),
    nextDay.getUTCMonth() + 1,
    nextDay.getUTCDate(),
    MORNING_HOUR,
    0,
  );
}

function nextSundayMorning(now: Date): Date {
  const wall = wallClockOf(now, appTz);
  const anchor = new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
  const ahead = (SUNDAY - anchor.getUTCDay() + 7) % 7 || 7;
  const target = new Date(anchor.getTime() + ahead * DAY);
  return instantFromWallClock(
    target.getUTCFullYear(),
    target.getUTCMonth() + 1,
    target.getUTCDate(),
    MORNING_HOUR,
    0,
  );
}

/** الأزرار الخمسة الموحدة في كل مواضع الغفوة: 10د / 1س / 3س / بكرة 9ص / الأحد. */
export function snoozeTargets(now: Date = new Date()): readonly SnoozeTarget[] {
  return [
    { id: '10m', label: '10 د', at: new Date(now.getTime() + 10 * MINUTE).toISOString() },
    { id: '1h', label: 'ساعة', at: new Date(now.getTime() + HOUR).toISOString() },
    { id: '3h', label: '3 ساعات', at: new Date(now.getTime() + 3 * HOUR).toISOString() },
    { id: 'tomorrow', label: 'بكرة 9ص', at: tomorrowMorning(now).toISOString() },
    { id: 'sunday', label: 'الأحد', at: nextSundayMorning(now).toISOString() },
  ];
}

/** غفوة سبب الانتباه بتاريخ انتهاء (D43/D66) — أفق أطول من غفوة التذكير. */
export function attentionSnoozeTargets(now: Date = new Date()): readonly SnoozeTarget[] {
  return [
    { id: 'tomorrow', label: 'بكرة', at: tomorrowMorning(now).toISOString() },
    { id: 'sunday', label: 'الأحد', at: nextSundayMorning(now).toISOString() },
    { id: 'week', label: 'أسبوع', at: new Date(now.getTime() + 7 * DAY).toISOString() },
  ];
}

/** موعد متابعة الانتظار (`follow_up_at`) — يغذي سبب WAITING_FOLLOWUP_DUE (D45). */
export function followUpTargets(now: Date = new Date()): readonly SnoozeTarget[] {
  return [
    { id: 'tomorrow', label: 'بكرة', at: tomorrowMorning(now).toISOString() },
    { id: '3d', label: 'بعد 3 أيام', at: new Date(now.getTime() + 3 * DAY).toISOString() },
    { id: 'week', label: 'بعد أسبوع', at: new Date(now.getTime() + 7 * DAY).toISOString() },
  ];
}
